import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  fetchQuizzes,
  resetCardQuizState,
  resetVideoQuizState,
  setSelectedQuiz,
  submitCardQuiz,
} from "../store/reducers/quizzesSlice";
import { getOrganizationById } from "../store/reducers/organizationSlice";
import { submitVideoQuiz } from "../store/reducers/quizzesSlice"; 

import { useHistory } from "react-router-dom";
import _ from "lodash";

const QuizList = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { loading, data } = useSelector((state) => state.quizzes.quizzes);
  const { videoQuiz, cardQuiz } = useSelector((state) => state.quizzes);
  const { organizationDetails } = useSelector((state) => state.organization);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedQuiz, setSelectedQuizLocal] = useState(null);
  const [activeQuizId, setActiveQuizId] = useState(null);

  useEffect(() => {
    if (!organizationDetails?.id) {
      const orgId = localStorage.getItem("organisation_id");
      if (orgId) {
        dispatch(getOrganizationById(orgId));
      }
    }
  }, [organizationDetails, dispatch]);

  useEffect(() => {
    if (organizationDetails?.id) {
      dispatch(fetchQuizzes(organizationDetails.id));
    }
  }, [dispatch, organizationDetails?.id]);

  useEffect(() => {
    const handler = _.debounce((value) => {
      setDebouncedSearch(value);
    }, 500);

    handler(search);
    return () => handler.cancel();
  }, [search]);

  const filteredData = data.filter((quiz) =>
    quiz.displayName?.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const getErrorMessage = (error, fallbackMessage) =>
    error?.error || error?.message || error || fallbackMessage;

  const closeModal = () => {
    if (videoQuiz.loading || cardQuiz.loading) {
      return;
    }

    setShowModal(false);
    setSelectedQuizLocal(null);
    dispatch(resetVideoQuizState());
    dispatch(resetCardQuizState());
  };

  const handleStart = async (quiz) => {
    if (!quiz) return;
  
    if (quiz.attempt !== 0) {
      dispatch(resetVideoQuizState());
      dispatch(resetCardQuizState());
      setSelectedQuizLocal(quiz);
      setShowModal(true);
      return;
    }
  
    try {
      setActiveQuizId(quiz.id);
      let hasValidId = false;
  
      let attemptPayload = {
        organization_id: organizationDetails?.id,
      };
  
      if (quiz?.video_id) {
        attemptPayload.video_id = quiz.video_id;
        hasValidId = true;
      } else if (quiz?.card_id) {
        attemptPayload.card_id = quiz.card_id;
        hasValidId = true;
      }

      dispatch(setSelectedQuiz(quiz));
      history.push(`/quizzes/${quiz.id}`);
    } catch (err) {
      toast.error("Unable to start quiz right now");
    } finally {
      setActiveQuizId(null);
    }
  };


  const handleYes = async () => {
    if (!selectedQuiz) return;
  
    try {
      setActiveQuizId(selectedQuiz.id);
      dispatch(resetVideoQuizState());
      dispatch(resetCardQuizState());
  
      let payload = {
        organization_id: organizationDetails?.id,
      };

      let action;
  
      if (selectedQuiz?.video_id) {
        payload.video_id = selectedQuiz.video_id;
        payload.want_video_quiz = true;
        action = submitVideoQuiz(payload);
      } else {
        payload.card_id = selectedQuiz.card_id;
        payload.want_card_quiz = true;
        action = submitCardQuiz(payload);
      }
  
      const res = await dispatch(action);

      if (submitVideoQuiz.rejected.match(res) || submitCardQuiz.rejected.match(res)) {
        toast.error(
          getErrorMessage(res.payload, "Unable to submit quiz request")
        );
        return;
      }

      dispatch(setSelectedQuiz(selectedQuiz));
  
      await dispatch(fetchQuizzes(organizationDetails.id));
  
      setShowModal(false);
      setSelectedQuizLocal(null);
  
      history.push(`/quizzes/${selectedQuiz.id}`);
    } catch (err) {
      toast.error("Unable to submit quiz request");
    } finally {
      setActiveQuizId(null);
    }
  };

  const isModalLoading = videoQuiz.loading || cardQuiz.loading;

  return (
    <div style={{ height: "93vh", overflow: "scroll" }}>
      <div className="row my-2 align-items-center">
        <div className="col-12 d-flex flex-column flex-md-row align-items-center justify-content-between">
          <div className="fw-bold mb-2 mb-md-0" style={{ minWidth: 120 }}>
            Quiz List
          </div>

          <div
            className="d-flex justify-content-center align-items-center gap-2 mx-auto"
            style={{ maxWidth: 600, width: "100%" }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Search quiz"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {search && (
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSearch("")}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <table className="table align-middle">
        <thead>
          <tr>
            <th>Sr. No</th>
            <th>Quiz Name</th>
            <th>Expire Date</th>
            <th style={{ textAlign: "center" }}>Action</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className="text-center">
                Loading...
              </td>
            </tr>
          ) : (
            filteredData.map((quiz, index) => (
              <tr key={quiz.id}>
                <td>{index + 1}</td>

                <td>
                  {quiz.displayName}
                  <br />
                  <small className="text-muted">
                    {quiz.displayName_dk}
                  </small>
                </td>

                <td>{quiz.expire_date || "No Expiry"}</td>

                <td className="text-center">
                  <button
                    className="btn btn-sm bg_color"
                    onClick={() => handleStart(quiz)}
                    disabled={activeQuizId === quiz.id}
                  >
                    {activeQuizId === quiz.id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Starting...
                      </>
                    ) : quiz.attempt === 0 ? (
                      "Start"
                    ) : (
                      "Re attempt"
                    )}
                  </button>
                </td>
              </tr>
            ))
          )}

          <tr hidden={filteredData.length !== 0 || loading}>
            <td colSpan="5" className="text-center">
              Data Not Found
            </td>
          </tr>
        </tbody>
      </table>

      {showModal && (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reattempt Quiz</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  disabled={isModalLoading}
                />
              </div>

              <div className="modal-body text-center">
                <p>Are you sure you want to reattempt this quiz? Make sure You have {3 - selectedQuiz.attempt} reattempt remaining</p>
              </div>

              <div className="modal-footer justify-content-center">
                <button
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={isModalLoading}
                >
                  No
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleYes}
                  disabled={isModalLoading}
                >
                  {isModalLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processing...
                    </>
                  ) : (
                    "Yes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizList;
